import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { DatabaseSettingsModel, DatabaseSettings } from '../services/DatabaseSettings';

// Mirrors web-ui/src/context/AppSettingsContext.tsx — keep the two in step (see
// the shared-code note in CLAUDE.md).

interface AppSettingsContextType {
    // The whole settings document, or null until the first snapshot lands.
    // Holding the document rather than individual fields means a new setting
    // needs no change here or in the model; consumers read the field they want.
    settings: DatabaseSettings | null;
}

const AppSettingsContext = createContext<AppSettingsContextType>({ settings: null });

export const useAppSettings = () => useContext(AppSettingsContext);

/**
 * The single live subscription to settings/settings. Consumers read the field
 * they need (e.g. subscriptionPrice) so a price change in Firestore reaches the
 * whole app without a reload and without any hard-coded fallback drifting out of
 * sync.
 */
export const AppSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<DatabaseSettings | null>(null);

    useEffect(() => {
        return DatabaseSettingsModel.subscribeSettings(setSettings);
    }, []);

    return (
        <AppSettingsContext.Provider value={{ settings }}>{children}</AppSettingsContext.Provider>
    );
};

export default AppSettingsContext;
